/**
 * Default Error Fallback Component
 * Used by ErrorBoundary when no custom fallback is provided
 */

import React from 'react';
import { Button } from '../ui/Button';
import { isDevelopment } from '../../utils/config';

interface ErrorFallbackProps {
    error: Error;
    reset: () => void;
}

export const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({ error, reset }) => {
    const handleReload = () => {
        window.location.reload();
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                    <svg
                        className="w-8 h-8 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                    </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Oops! Something went wrong
                </h1>

                <p className="text-gray-600 mb-6">
                    We're sorry for the inconvenience. The page encountered an unexpected error.
                </p>

                {isDevelopment && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                        <h3 className="font-semibold text-red-800 mb-2">Error Details (Development Mode):</h3>
                        <p className="text-red-700 text-sm font-mono break-all">
                            {error.message}
                        </p>
                        {error.stack && (
                            <details className="mt-2">
                                <summary className="text-red-800 font-semibold cursor-pointer">Stack Trace</summary>
                                <pre className="text-red-700 text-xs mt-2 overflow-auto max-h-32">
                                    {error.stack}
                                </pre>
                            </details>
                        )}
                    </div>
                )}

                <div className="space-y-3">
                    <Button
                        onClick={reset}
                        className="w-full"
                    >
                        Try Again
                    </Button>

                    <Button
                        onClick={handleReload}
                        variant="outline"
                        className="w-full"
                    >
                        Reload Page
                    </Button>

                    <Button
                        onClick={() => window.location.href = '/'}
                        variant="ghost"
                        className="w-full"
                    >
                        Go Home
                    </Button>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                        If this problem persists, please{' '}
                        <a
                            href="/contact"
                            className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                            contact our support team
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};